import React, { FC, ReactNode, useContext, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useTour } from '@reactour/tour';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import { HeaderRight } from '../../../layout/Header/Header';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from '../../../components/bootstrap/OffCanvas';
import Alert from '../../../components/bootstrap/Alert';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '../../../components/bootstrap/Modal';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../../components/bootstrap/Dropdown';
import Icon from '../../../components/icon/Icon';
import ThemeContext from '../../../contexts/themeContext';
import AuthContext from '../../../contexts/authContext';
import useDarkMode from '../../../hooks/useDarkMode';
import Popovers from '../../../components/bootstrap/Popovers';
import Spinner from '../../../components/bootstrap/Spinner';
import { useWizardContext } from '../../../contexts/wizardContext';
import useDeviceScreen from '../../../hooks/useDeviceScreen';
import { dashboardPagesMenu, demoPagesMenu } from '../../../menu';

interface ICommonHeaderRightProps {
	beforeChildren?: ReactNode;
	afterChildren?: ReactNode;
}
const CommonHeaderRight: FC<ICommonHeaderRightProps> = ({ beforeChildren, afterChildren }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { authData, setAuthData, setUser, authUser } = useContext(AuthContext);
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const wizardContext = useWizardContext(); // Get wizard context (may be undefined if not in Wizard)
	const deviceScreen = useDeviceScreen();
	// @ts-ignore
	const isMobile = deviceScreen?.width <= process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE;

	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const isLoginPage = location.pathname === demoPagesMenu.login.path;
	const isLoggedIn = !!authData?.token?.accessToken;
	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const [offcanvasStatus, setOffcanvasStatus] = useState(false);
	const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);
	const [hoverMenuOpen, setHoverMenuOpen] = useState(false);

	const { i18n } = useTranslation();

	/**
	 * Language attribute
	 */
	useLayoutEffect(() => {
		document.documentElement.setAttribute('lang', i18n.language.substring(0, 2));
	});

	const { setIsOpen } = useTour();

	// Handler to show confirmation modal
	const handleSaveExitClick = () => {
		setShowExitConfirmModal(true);
	};

	// Handler to cancel and stay on current step
	const handleCancelExit = () => {
		setShowExitConfirmModal(false);
	};

	// Handler to confirm and execute the actual save & exit
	const handleConfirmExit = () => {
		setShowExitConfirmModal(false);
		if (wizardContext?.handleExit) {
			wizardContext.handleExit();
		}
	};

	// Handler for guideline menu items
	const handleGuidelineClick = (guidelineType: 'individual' | 'joint') => {
		if (guidelineType === 'individual') {
			// Open Individual BO Account guide in a new tab
			window.open(dashboardPagesMenu.individualNote.path, '_blank');
		} else if (guidelineType === 'joint') {
			// Open Joint BO Account guide in a new tab
			window.open(dashboardPagesMenu.jointNote.path, '_blank');
		}
	};

	// Handler for hover menu items
	const handleHoverMenuClick = (menuType: 'option1' | 'option2') => {
		if (menuType === 'option1') {
			// Open option 1 in a new tab - customize this path
			window.open(dashboardPagesMenu.individualNote.path, '_blank');
		} else if (menuType === 'option2') {
			// Open option 2 in a new tab - customize this path
			window.open(dashboardPagesMenu.jointNote.path, '_blank');
		}
	};

	const handleLoginClick = () => {
		try {
			sessionStorage.removeItem('acstatus');
		} catch {
			// ignore sessionStorage errors
		}
		navigate(demoPagesMenu.login.path);
	};

	const handleLogout = () => {
		if (setAuthData) {
			setAuthData(null);
		}
		if (setUser) {
			// Clear any legacy username
			setUser('');
		}
		navigate(demoPagesMenu.login.path);
	};

	return (
		<HeaderRight>
			<div className='row g-3'>
				{beforeChildren}
				{/* Guideline Menu - Visible on all screens including mobile */}
{/* 				<div className='col-auto'>
					<Popovers trigger={isMobile ? 'click' : 'hover'} desc='Guidelines'>
						<Dropdown>
							<DropdownToggle hasIcon={false}>
									<Button
										color={darkModeStatus ? 'dark' : 'light'}
										hoverShadow='default'
										isLight={!darkModeStatus}
										size='lg'
										aria-label='Guidelines'>
										Guidelines
									</Button>
							</DropdownToggle>
							<DropdownMenu isAlignmentEnd>
								<DropdownItem>
									<Button
										icon='Person'
										onClick={() => handleGuidelineClick('individual')}
										color={darkModeStatus ? 'dark' : 'light'}
										isLight={!darkModeStatus}
										className='w-100 text-start small'>
										<span className='d-block d-md-inline'>
											How to Open Individual BO Account
										</span>
									</Button>
								</DropdownItem>
								<DropdownItem>
									<Button
										icon='People'
										onClick={() => handleGuidelineClick('joint')}
										color={darkModeStatus ? 'dark' : 'light'}
										isLight={!darkModeStatus}
										className='w-100 text-start small'>
										<span className='d-block d-md-inline'>
											How to Open Joint BO Account
										</span>
									</Button>
								</DropdownItem>
							</DropdownMenu>
						</Dropdown>
					</Popovers>
				</div> */}

				{/* Hover Menu - Desktop hover menu system (hidden on Login page and when logged in) */}
				{!isMobile && !isLoginPage && !isLoggedIn && (
					<div
						className='col-auto position-relative d-flex align-items-center'
						onMouseEnter={() => setHoverMenuOpen(true)}
						onMouseLeave={() => setHoverMenuOpen(false)}>
						<span
							className={classNames({
								'text-white': darkModeStatus,
								'text-dark': !darkModeStatus,
							})}
							style={{
								cursor: 'pointer',
								display: 'inline-block',
								fontSize: '1.25rem',
								lineHeight: '1.5',
								transition: 'opacity 0.2s ease-in-out',
							}}
							aria-label='Menu'>
							Guidelines
						</span>
						<div
							className={classNames(
								'position-absolute',
								'end-0',
								'p-2',
								'rounded',
								'shadow',
								{
									'bg-dark': darkModeStatus,
									'bg-light': !darkModeStatus,
								},
							)}
							style={{
								top: '100%',
								// marginTop: '0.25rem',
								minWidth: '200px',
								maxWidth: 'min(300px, calc(100vw - 2rem))',
								width: 'max-content',
								zIndex: 1000,
								border: darkModeStatus ? '1px solid #495057' : '1px solid #dee2e6',
								whiteSpace: 'normal',
								opacity: hoverMenuOpen ? 1 : 0,
								visibility: hoverMenuOpen ? 'visible' : 'hidden',
								transform: hoverMenuOpen ? 'translateY(0)' : 'translateY(-10px)',
								transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out, visibility 0.3s ease-in-out',
								pointerEvents: hoverMenuOpen ? 'auto' : 'none',
								right: 0,
							}}>
								<div
									role='button'
									tabIndex={0}
									className={classNames('p-2', 'rounded', {
										'text-white': darkModeStatus,
										'text-dark': !darkModeStatus,
									})}
									style={{
										cursor: 'pointer',
										backgroundColor: 'transparent',
										whiteSpace: 'normal',
										wordWrap: 'break-word',
										transition: 'background-color 0.2s ease-in-out, transform 0.2s ease-in-out',
										marginBottom: '0.5rem',
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.backgroundColor = darkModeStatus
											? '#495057'
											: '#e9ecef';
										e.currentTarget.style.transform = 'translateX(5px)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.backgroundColor = 'transparent';
										e.currentTarget.style.transform = 'translateX(0)';
									}}
									onClick={() => handleHoverMenuClick('option1')}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleHoverMenuClick('option1');
										}
									}}>
									<div className='d-flex align-items-center'>
										<Icon
											icon='Person'
											size='sm'
											color={darkModeStatus ? 'info' : 'primary'}
											className='me-2 flex-shrink-0'
											style={{
												transition: 'transform 0.2s ease-in-out',
											}}
										/>
										<span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
										How to open an individual bo account
										</span>
									</div>
								</div>
								<div
									role='button'
									tabIndex={0}
									className={classNames('p-2', 'rounded', {
										'text-white': darkModeStatus,
										'text-dark': !darkModeStatus,
									})}
									style={{
										cursor: 'pointer',
										backgroundColor: 'transparent',
										whiteSpace: 'normal',
										wordWrap: 'break-word',
										transition: 'background-color 0.2s ease-in-out, transform 0.2s ease-in-out',
										marginTop: '0.5rem',
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.backgroundColor = darkModeStatus
											? '#495057'
											: '#e9ecef';
										e.currentTarget.style.transform = 'translateX(5px)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.backgroundColor = 'transparent';
										e.currentTarget.style.transform = 'translateX(0)';
									}}
									onClick={() => handleHoverMenuClick('option2')}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleHoverMenuClick('option2');
										}
									}}>
									<div className='d-flex align-items-center'>
										<Icon
											icon='People'
											size='sm'
											color={darkModeStatus ? 'info' : 'primary'}
											className='me-2 flex-shrink-0'
											style={{
												transition: 'transform 0.2s ease-in-out',
											}}
										/>
										<span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
										How to open a joint bo account
										</span>
									</div>
								</div>
							</div>
					</div>
				)}

				{/* Dark Mode */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Dark / Light mode'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							onClick={() => setDarkModeStatus(!darkModeStatus)}
							className='btn-only-icon'
							data-tour='dark-mode'
							aria-label='Toggle dark mode'>
							<Icon
								icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
								color={darkModeStatus ? 'info' : 'warning'}
								className='btn-icon'
							/>
						</Button>
					</Popovers>
				</div>

				{/*	Full Screen - Hidden on mobile and on Login page */}
{/* 				{!isMobile && !isLoginPage && (
					<div className='col-auto'>
						<Popovers trigger='hover' desc='Fullscreen'>
							<Button
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...styledBtn}
								icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
								onClick={() => setFullScreenStatus(!fullScreenStatus)}
								aria-label='Toggle fullscreen'
							/>
						</Popovers>
					</div>
				)} */}
				{/* Home - only on Login page */}
				{isLoginPage && (
					<div className='col-auto'>
							<Button
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...styledBtn}
								icon='Home'
								onClick={() => navigate(dashboardPagesMenu.wizard.path)}
								className='btn-only-icon'
								aria-label='Home'
							/>
					</div>
				)}

				{/* Login - hidden on Login page and when already logged in */}
				{!isLoginPage && !isLoggedIn && (
					<div className='col-auto'>
						<Popovers trigger='hover' desc='Login'>
							<Button
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...styledBtn}
								icon='Person'
								onClick={handleLoginClick}
								className='btn-only-icon'
								aria-label='Login'
							/>
						</Popovers>
					</div>
				)}

				{/* Profile menu - only when logged in */}
				{isLoggedIn && (
					<div className='col-auto'>
						<Dropdown>
							<DropdownToggle hasIcon={false}>
								<Button
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...styledBtn}
									className='btn-only-icon rounded-circle border-0'
									aria-label='User menu'>
									<div
										className={classNames(
											'd-flex',
											'align-items-center',
											'justify-content-center',
											'rounded-circle',
											{
												'bg-primary text-white': !darkModeStatus,
												'bg-info text-dark': darkModeStatus,
											},
										)}
										style={{ width: 32, height: 32, fontSize: '0.9rem' }}>
										{(authUser?.name || authUser?.userName || 'U')[0]}
									</div>
								</Button>
							</DropdownToggle>
							<DropdownMenu isAlignmentEnd className='p-2'>
								{authUser && (
									<>
										<DropdownItem isText className='px-2 py-2'>
											<div className='d-flex align-items-center'>
												<div
													className={classNames(
														'rounded-circle',
														'd-flex',
														'align-items-center',
														'justify-content-center',
														'me-2',
														{
															'bg-primary text-white': !darkModeStatus,
															'bg-info text-dark': darkModeStatus,
														},
													)}
													style={{ width: 36, height: 36, fontSize: '0.9rem' }}>
													{(authUser.name || authUser.userName || 'U')[0]}
												</div>
												<div>
													<div className='fw-semibold small'>{authUser.name}</div>
													<div className='text-muted small'>{authUser.email}</div>
												</div>
											</div>
										</DropdownItem>
										<DropdownItem isDivider />
									</>
								)}
								<DropdownItem>
									<Button
										color={darkModeStatus ? 'dark' : 'light'}
										isLight={!darkModeStatus}
										className='w-100 d-flex align-items-center gap-2 text-start'>
										<Icon icon='Person' size='sm' />
										<span>View profile</span>
									</Button>
								</DropdownItem>
								<DropdownItem>
									<Button
										color='danger'
										isLight
										className='w-100 d-flex align-items-center gap-2 text-start'
										onClick={handleLogout}>
										<Icon icon='Logout' size='sm' className='me-1' />
										<span>Logout</span>
									</Button>
								</DropdownItem>
							</DropdownMenu>
						</Dropdown>
					</div>
				)}

				{/* Save & Exit Button - Only shown in Wizard context */}
				{wizardContext?.shouldShowExitButton && wizardContext?.handleExit && (
					<div className='col-auto d-flex align-items-center'>
						<Button
							icon='Send'
							color='success'
							className='btn-sm'
							isLight
							onClick={handleSaveExitClick}
							isDisable={wizardContext.isDisabled}
							aria-label='Save and exit'>
							Save & Exit
						</Button>
					</div>
				)}

				{afterChildren}
			</div>

			<OffCanvas
				id='notificationCanvas'
				titleId='offcanvasExampleLabel'
				placement='end'
				isOpen={offcanvasStatus}
				setOpen={setOffcanvasStatus}>
				<OffCanvasHeader setOpen={setOffcanvasStatus}>
					<OffCanvasTitle id='offcanvasExampleLabel'>Notifications</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					<Alert icon='ViewInAr' isLight color='info' className='flex-nowrap'>
						4 new components added.
					</Alert>
					<Alert icon='ThumbUp' isLight color='warning' className='flex-nowrap'>
						New products added to stock.
					</Alert>
					<Alert icon='Inventory2' isLight color='danger' className='flex-nowrap'>
						There are products that need to be packaged.
					</Alert>
					<Alert icon='BakeryDining' isLight color='success' className='flex-nowrap'>
						Your food order is waiting for you at the consultation.
					</Alert>
					<Alert icon='Escalator' isLight color='primary' className='flex-nowrap'>
						Escalator will turn off at 6:00 pm.
					</Alert>
				</OffCanvasBody>
			</OffCanvas>

			{/* Save & Exit Confirmation Modal */}
			<Modal
				isOpen={showExitConfirmModal}
				setIsOpen={setShowExitConfirmModal}
				titleId='exitConfirmModal'
				isCentered
				isStaticBackdrop>
				<ModalHeader className='bg-warning' setIsOpen={setShowExitConfirmModal}>
					<ModalTitle id='exitConfirmModal'>
						<Icon icon='Warning' className='me-2' color='info' />
						Confirm Save & Exit
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<p className='text-muted'>
						Kindly complete all required fields before exiting to avoid losing any
						unsaved data (অনুগ্রহ করে বের হওয়ার আগে সকল প্রয়োজনীয় ঘর পূরণ করুন,
						অন্যথায় অসম্পাদিত তথ্য হারিয়ে যেতে পারে।).
					</p>
					<p className='text-muted'>
						Do you want to continue? (আপনি কি চালিয়ে যেতে চান?)
					</p>
				</ModalBody>
				<ModalFooter>
					<Button color='danger' onClick={handleCancelExit}>
						Cancel
					</Button>
					<Button color='success' onClick={handleConfirmExit}>
						OK
					</Button>
				</ModalFooter>
			</Modal>
		</HeaderRight>
	);
};
CommonHeaderRight.propTypes = {
	beforeChildren: PropTypes.node,
	afterChildren: PropTypes.node,
};
CommonHeaderRight.defaultProps = {
	beforeChildren: null,
	afterChildren: null,
};

export default CommonHeaderRight;
